import { FC, useContext, useEffect, useState } from "react";
import { Switch, Route } from "react-router-dom";
import { Home } from "./home";
import Progress from "./Progress";
import Layout from "./Layout";
import { type ViewMode } from "./chat/ViewToggle";
import { WalterContext } from "@src/taskpane/providers/walter/index.tsx";
import { defaultSelectionDetails } from "@src/taskpane/providers/walter/app-state.ts";
import { useInputOptions } from "@src/taskpane/hooks/useInputOptions";
import { useOnSelectionEvent } from "@src/taskpane/hooks/useOnSelectionEvent";
import { useOnDeleteEvent } from "@src/taskpane/hooks/useOnDeleteEvent";
import { syncEngine } from "@src/lib/sync-engine/SyncEngine";
import { useAppReady } from "@src/taskpane/hooks/useAppReady";
import { Link } from "@fluentui/react-components";
import { AddField } from "./fields";
import { useFields } from "@src/taskpane/hooks/useFields";
import { captureErrorInfo } from "@src/taskpane/helpers/errorHandler";
import ManageFieldSets from "@src/taskpane/components/FieldSets/ManageFieldSets";
import { NewFieldSet } from "@src/taskpane/components/FieldSets/NewFieldSet";
import { ChooseFieldSet } from "@src/taskpane/components/FieldSets/ChooseFieldSet";
import useToasts from "@src/taskpane/hooks/useToasts";
import { documentSchemaStore } from "@src/lib/schemas";
import { SelectionDetails } from "@src/lib/sync-engine/operations/GetSelection";
import { DynamicElement } from "@src/lib/liquidx/DynamicElement";
import { SearchAndSelect } from "@src/lib/sync-engine/SearchAndSelect";
import { useFeatureFlags } from "@src/taskpane/hooks/useFeatureFlags";
import { EnableTrackChanges } from "@src/lib/sync-engine/operations/EnableTrackChanges";

const VIEW_MODE_STORAGE_KEY = "word-addin-view-mode";

const getSavedViewMode = (chatEnabled: boolean | null): ViewMode => {
  // If chat is not enabled, always use automation
  if (!chatEnabled) return "automation";

  const saved = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
  if (saved === "chat" || saved === "automation") {
    return saved;
  }

  return chatEnabled ? "chat" : "automation";
};

const App: FC = () => {
  const { inputOptions, loadSchema } = useInputOptions();
  const { isAppReady: appReady } = useAppReady();
  const { setDocumentSelectionData, setReplacementsCount, setReplacementsCompleteCount, setReplacementsLoading } =
    useContext(WalterContext);
  const { showErrors, showCallToAction } = useToasts();
  const [migrating, setMigrating] = useState(false);
  const { isLoggedIn } = useContext(WalterContext);
  const featureFlagManager = useFeatureFlags();

  // Check if chat feature is enabled (async)
  const [isChatEnabled, setIsChatEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    if (isLoggedIn) {
      featureFlagManager
        .isEnabled("ai_word_addin")
        .then(setIsChatEnabled)
        .catch(() => setIsChatEnabled(false));
    }
  }, [isLoggedIn, featureFlagManager]);

  // Determine the effective view mode
  // Use automation as default while loading to prevent flicker
  const viewMode = isChatEnabled === null ? "automation" : getSavedViewMode(isChatEnabled);

  // Determine if we're in automation mode (not chat mode)
  const isAutomationMode = viewMode === "automation";

  // Only run automation hooks when in automation mode
  useOnSelectionEvent({ enabled: isAutomationMode });
  useOnDeleteEvent({ enabled: isAutomationMode });
  useFields({ enabled: isAutomationMode });

  // Listen for changes to the document selection (automation mode only)
  useEffect(() => {
    if (!appReady || !isAutomationMode) return;

    function handleSelectionChanged(value?: SelectionDetails) {
      setDocumentSelectionData(value ?? defaultSelectionDetails);
    }

    syncEngine.addSelectionChangeEventListener(handleSelectionChanged);
    return () => syncEngine.removeSelectionChangeEventListener(handleSelectionChanged);
  }, [setDocumentSelectionData, appReady, isAutomationMode]);

  // Setup syncEngine error handlers (automation mode only)
  useEffect(() => {
    if (!appReady || !isAutomationMode) return;

    // Display toast when an operation fails
    syncEngine.onFatalError((e) => {
      const message = JSON.stringify(e);

      showErrors({
        title: "Error updating document",
        messages: import.meta.env.DEV ? [message] : [],
        footerContent: <Link onClick={() => window.location.reload()}>Reload</Link>,
      });

      captureErrorInfo(e);
    });

    syncEngine.onFailedOperation((operation, e) => {
      const errorMessages = import.meta.env.DEV
        ? [`Unable to apply operation ${operation.constructor.name}`, JSON.stringify(e)]
        : [];

      showErrors({
        title: "Error updating document",
        messages: errorMessages,
        footerContent: <Link onClick={() => window.location.reload()}>Reload</Link>,
      });

      captureErrorInfo(e);
    });

    syncEngine.afterInsert((operation) => {
      if (operation.replacedText.length > 0) {
        showCallToAction(
          "Replace all?",
          "Would you like to replace all occurrences of “" + operation.replacedText + "” with this field?",
          [
            {
              text: "Yes, replace all",
              onClick: async () => {
                if (operation.insertedContentControlId) {
                  const element = DynamicElement.find(operation.insertedContentControlId);
                  if (element) {
                    await syncEngine.perform(new EnableTrackChanges());
                    await SearchAndSelect.run(
                      operation.replacedText,
                      async (_) => {
                        await element.duplicate().save();
                      },
                      ({ total, current, loading }) => {
                        setReplacementsCount(total);
                        setReplacementsCompleteCount(current + 1);
                        setReplacementsLoading(loading);
                      },
                    );

                    setReplacementsCount(0);
                    setReplacementsCompleteCount(0);
                    setReplacementsLoading(false);
                  }
                } else {
                  showErrors({
                    title: "Error updating document",
                    messages: ["Unable to find inserted content control"],
                  });
                }
              },
            },
          ],
        );
      }
    });
  }, [appReady, isAutomationMode]);

  // Start syncEngine (automation mode only)
  useEffect(() => {
    if (!appReady || !isAutomationMode) return;

    syncEngine.start();
    return () => syncEngine.stop();
  }, [appReady, isAutomationMode]);

  // Update the input options when the focused fields changes (automation mode only)
  useEffect(() => {
    if (!appReady || !isAutomationMode) return;

    function reloadInputs() {
      loadSchema(documentSchemaStore.schema);
    }

    reloadInputs();
    documentSchemaStore.addEventListener("change", reloadInputs);
    return () => documentSchemaStore.removeEventListener("change", reloadInputs);
  }, [appReady, isAutomationMode]);

  // Only require appReady and inputOptions when in automation mode and logged in
  if (isAutomationMode && isLoggedIn && (!appReady || !inputOptions)) {
    return (
      <Layout>
        <Progress message="Initializing..." />
      </Layout>
    );
  }

  if (migrating) {
    return (
      <Layout>
        <Progress message="Upgrading..." />
      </Layout>
    );
  }

  return (
    <Layout>
      <Switch>
        <Route exact path="/">
          <Home setMigrating={setMigrating} />
        </Route>
        <Route exact path="/choose-field-set">
          <ChooseFieldSet />
        </Route>
        <Route exact path="/fields/:key">
          <AddField />
        </Route>
        <Route exact path="/field-sets">
          <ManageFieldSets />
        </Route>
        <Route exact path="/field-sets/new">
          <NewFieldSet />
        </Route>
        <Route>
          <div style={{ padding: "12px" }}>
            <h1>404 - Not found</h1>
          </div>
        </Route>
      </Switch>
    </Layout>
  );
};

export default App;
